import {
  CredentialBroker, EventType, GatewayAuthAPI, HermesAPI, HermesClient, Welcome, demoEndpoint, endpointKey,
  methodNotFound, obj, relayHub, requestSessionID, sameEndpoint, str,
  type ConnectionState, type GatewayCredential, type GatewayEndpoint, type GatewayEvent, type ProfileSummary,
  type RelayPairing, type RelayRoute, type RelayWelcome, type ServerRequest, type ServerStatus, type SessionSummary,
  type SlashCommand,
} from "@timibot/core";
import { ChatStore } from "./chat";
import { Observable } from "./observable";
import { CredentialVault, Preferences, TranscriptCache, connectionID, isGuestProfile, type ServerProfile } from "./persistence";
import { ProfileStore } from "./profiles";
import { SessionListStore } from "./sessions";
import { AssetStore, AutomationFeed } from "./workspace";

export type Route = "onboarding" | "main";
export type MainTab = "chat" | "workspace";
export type FeatureID = "skills" | "bots" | "settings";
export type ConnectMode = "firstRun" | "reauth" | "add";

export interface Toast { id: number; text: string }

let toastCounter = 0;

/** App 的根状态：连接生命周期、事件路由、对话管理、前后台切换（对应 iOS 的 AppModel）。 */
export class AppModel extends Observable {
  route: Route;
  server?: ServerProfile;
  /** 其它已配对的连接（设置 → 连接里切换） */
  savedServers: ServerProfile[] = Preferences.savedServers;
  /** 最近一次握手时 connector 告诉我们的信息（版本、身份） */
  hostWelcome?: RelayWelcome;
  connection: ConnectionState = { kind: "idle" };
  serverStatus?: ServerStatus;
  client?: HermesClient;
  api?: HermesAPI;

  readonly sessions = new SessionListStore();
  readonly profiles = new ProfileStore();
  /** 作品区（对话里产出的图片、视频、文件） */
  readonly assets = new AssetStore();
  /** 工作台“长期目标”：定时任务最近每次运行做了什么 */
  readonly automationFeed = new AutomationFeed();

  chats: ChatStore[] = [];
  activeChat: ChatStore;
  selectedProfile?: string;

  toast?: Toast;
  isSidebarOpen = false;
  selectedTab: MainTab = "chat";
  presentedFeature?: FeatureID;
  showReauth = false;
  connectMode: ConnectMode = "reauth";
  /** 粘贴进来、等待配对页处理的配对链接 */
  pendingPairing?: RelayPairing;
  isInForeground = typeof document === "undefined" || document.visibilityState === "visible";
  /** cron.changed 事件计数，自动化页面据此刷新 */
  cronRevision = 0;
  /** Bot 的电脑：控制权变了、安装结束时 +1，工作台的卡片据此刷新状态 */
  displayRevision = 0;
  /** 正在安装桌面组件时的输出（最后几行） */
  displayInstallLog: string[] = [];
  /** 安装桌面组件要 sudo 密码（免密 sudo 的主机不会问） */
  displaySudoRequest?: ServerRequest;
  /** 斜杠命令目录（输入框联想用） */
  slashCommands: SlashCommand[] = [];
  lastConnectDuration?: number;

  private unsubscribers: (() => void)[] = [];
  private toastTimer?: ReturnType<typeof setTimeout>;
  private unclaimedRequests: ServerRequest[] = [];
  private hasLoadedOnce = false;

  constructor() {
    super();
    relayHub.deviceName = deviceName();
    const saved = Preferences.server;
    const credential = CredentialVault.load();
    this.selectedProfile = Preferences.selectedProfile;
    this.server = saved;
    this.assets.use(this.workspaceScope);
    this.route = saved && credential ? "main" : "onboarding";
    this.activeChat = this.makeChat(this.selectedProfile);
    if (this.route === "main" && saved && credential) {
      this.start(saved.endpoint, credential);
      // 体验模式的数据只在内存里，重新打开后上次的对话已经不在了
      const last = Preferences.lastSessionID;
      if (last && saved.endpoint.kind !== "demo") this.openStored(last, this.selectedProfile);
    }
    this.watchVisibility();
  }

  /** 当前连接是别人分享的 Bot：只能用分享的 Bot，不能新建/修改 Bot、改模型和技能 */
  get isGuest(): boolean { return isGuestProfile(this.server); }
  /** 体验模式：没有主机，用本地的示例数据看看界面 */
  get isDemo(): boolean { return this.server?.endpoint.kind === "demo"; }
  get botTitle(): string { return this.profiles.title(this.selectedProfile); }

  startDemo(): void {
    this.completeSignIn(demoEndpoint, { type: "tunnel" }, undefined, undefined, undefined);
  }

  // MARK: 连接

  private start(endpoint: GatewayEndpoint, credential: GatewayCredential): void {
    this.stopClient();
    const broker = new CredentialBroker(endpoint, credential, (updated) => CredentialVault.save(updated));
    const client = new HermesClient(broker);
    this.client = client;
    this.api = new HermesAPI(client);
    // 经中转时先把隧道建起来（TLS + 端到端握手），和下面的订阅、状态查询并行
    if (endpoint.kind === "relay") relayHub.prewarm(endpoint.relay);
    // 连接任务还没真正跑起来前就显示“连接中”，避免启动时闪一下“未连接”
    this.connection = { kind: "connecting", attempt: 0 };
    // 先订阅再连接：服务端请求没人订阅会被直接拒绝
    this.unsubscribers = [
      client.states.subscribe((s) => this.connectionChanged(s)),
      client.events.subscribe((e) => this.handleEvent(e)),
      client.serverRequests.subscribe((r) => this.handleRequest(r)),
    ];
    client.connect();
    new GatewayAuthAPI(endpoint).status().then((s) => { this.serverStatus = s; this.changed(); }, () => {});
    this.changed();
  }

  private stopClient(): void {
    for (const off of this.unsubscribers) off();
    this.unsubscribers = [];
    this.client?.disconnect();
    this.client = undefined;
    this.api = undefined;
  }

  private connectionChanged(state: ConnectionState): void {
    this.connection = state;
    this.changed();
    if (state.kind === "connected") {
      this.lastConnectDuration = state.hello.connectDuration;
      void this.refreshAfterConnect();
    } else if (state.kind === "needsLogin") {
      void this.recoverLogin();
    }
  }

  private async refreshAfterConnect(): Promise<void> {
    const api = this.api;
    // 互不依赖的几件事并行做：每一件都是一次网络往返，串行会把首屏时间叠加起来
    const catalog = api?.commandsCatalog(undefined, this.selectedProfile).catch(() => undefined);
    await Promise.all([
      this.profiles.refresh(api),
      this.sessions.refresh(api, this.selectedProfile),
      this.activeChat.loadIfNeeded(),
    ]);
    const entries = await catalog;
    if (entries) { this.slashCommands = entries; this.changed(); }
    // 选中的 Bot 不在了（或者是访客、没有默认 Bot）：换成默认 Bot，没有默认就选第一个
    if (this.profiles.profiles.length && !this.profiles.profile(this.selectedProfile)) {
      const fallback = this.profiles.profiles.find((p) => p.is_default) ?? this.profiles.profiles[0];
      this.selectProfile(this.profiles.apiName(fallback));
    }
    if (!this.hasLoadedOnce) this.hasLoadedOnce = true;
    this.adoptRelayAddressChange();
    this.adoptRole();
  }

  /** 主人在主机上改了这台设备的身份（例如又分享了一个 Bot 给它）：以握手时 connector 说的为准 */
  private adoptRole(): void {
    const server = this.server;
    if (!server || server.endpoint.kind !== "relay") return;
    const welcome = relayHub.tunnel(server.endpoint.relay).welcome;
    if (!welcome) return;
    this.hostWelcome = welcome;
    const role = welcome.role ?? "owner";
    if (role !== (server.role ?? "owner") || JSON.stringify(welcome.profiles) !== JSON.stringify(server.bots)) {
      this.server = { ...server, role, bots: welcome.profiles };
      Preferences.server = this.server;
    }
    this.changed();
  }

  /** connector 换了中转地址：更新保存的地址，下次直接用新地址，不用重新配对 */
  private adoptRelayAddressChange(): void {
    const server = this.server;
    if (!server || server.endpoint.kind !== "relay") return;
    const relay = server.endpoint.relay;
    const advertised = relayHub.tunnel(relay).welcome?.relay;
    if (!advertised || !advertised.startsWith("https://") || advertised === relay.relayURL) return;
    this.server = { ...server, endpoint: { kind: "relay", relay: { ...relay, relayURL: advertised } } };
    Preferences.server = this.server;
    this.changed();
  }

  /**
   * 凭据被拒时先看主机现在要不要登录：主机关掉了登录门禁就自动改用免登录凭据重连，不打扰用户；
   * 否则弹出重新连接页面。
   */
  private async recoverLogin(): Promise<void> {
    const server = this.server;
    if (server?.endpoint.kind === "relay") {
      try {
        const welcome = await relayHub.tunnel(server.endpoint.relay).pair(undefined);
        if (!Welcome.requiresLogin(welcome) && !welcome.backend_error) {
          this.completeSignIn(server.endpoint, { type: "tunnel" }, undefined, undefined, this.serverStatus);
          return;
        }
      } catch { /* 下面弹出重新连接 */ }
    }
    this.presentedFeature = undefined;
    this.connectMode = "reauth";
    this.showReauth = true;
    this.changed();
  }

  retryNow(): void {
    this.client?.reconnectNow();
  }

  presentConnect(mode: ConnectMode): void {
    this.presentedFeature = undefined;
    this.connectMode = mode;
    this.showReauth = true;
    this.changed();
  }

  dismissConnect(): void {
    this.showReauth = false;
    this.changed();
  }

  // MARK: 登录 / 退出

  completeSignIn(endpoint: GatewayEndpoint, credential: GatewayCredential, username: string | undefined,
    provider: string | undefined, status: ServerStatus | undefined, welcome?: RelayWelcome): void {
    const same = sameEndpoint(this.server?.endpoint, endpoint);
    const profile: ServerProfile = {
      endpoint, username, authProvider: provider, lastKnownVersion: status?.version,
      role: welcome?.role ?? (same ? this.server?.role : undefined),
      bots: welcome?.profiles ?? (same ? this.server?.bots : undefined),
    };
    if (welcome) this.hostWelcome = welcome;
    this.activate(profile, credential, status);
  }

  /** 切到另一条已保存的连接（例如从自己的主机切到朋友分享的 Bot） */
  switchConnection(target: ServerProfile): void {
    const credential = CredentialVault.load(connectionID(target)) ?? { type: "tunnel" as const };
    this.activate(target, credential, undefined);
  }

  /** 删掉一条不在用的连接：丢掉本机密钥和凭据（主机那边的授权由主人管理） */
  forgetConnection(target: ServerProfile): void {
    this.savedServers = this.savedServers.filter((x) => connectionID(x) !== connectionID(target));
    Preferences.savedServers = this.savedServers;
    CredentialVault.save(undefined, connectionID(target));
    if (target.endpoint.kind === "relay") relayHub.forget(target.endpoint.relay);
    this.changed();
  }

  /** 配对链接用哪把设备密钥：同一台主机上，主人和访客是两个身份（两把密钥），各自保留、互不覆盖 */
  routeForPairing(pairing: RelayPairing): RelayRoute {
    const route: RelayRoute = { ...pairing.route };
    const all = [...(this.server ? [this.server] : []), ...this.savedServers];
    const sameHost = all.filter((x) => x.endpoint.kind === "relay" && x.endpoint.relay.hostID === route.hostID);
    const wantGuest = pairing.sharedBot !== undefined;
    const existing = sameHost.find((x) => isGuestProfile(x) === wantGuest);
    if (existing && existing.endpoint.kind === "relay") route.keyID = existing.endpoint.relay.keyID;
    else if (sameHost.length) route.keyID = `${route.hostID}.${wantGuest ? "guest" : "owner"}`;
    if (wantGuest) {
      const title = pairing.botTitle ?? pairing.sharedBot;
      if (title) route.name = `${title} · ${route.name}`;
    }
    return route;
  }

  private activate(profile: ServerProfile, credential: GatewayCredential, status: ServerStatus | undefined): void {
    const old = this.server;
    const same = sameEndpoint(old?.endpoint, profile.endpoint);
    if (old && !same && old.endpoint.kind !== "demo") {
      // 原来的连接留着，随时切回去（体验模式不留）
      CredentialVault.save(CredentialVault.load(), connectionID(old));
      this.savedServers = [old, ...this.savedServers.filter((x) => connectionID(x) !== connectionID(old))];
      if (old.endpoint.kind === "relay") relayHub.tunnel(old.endpoint.relay).suspend();
    }
    this.savedServers = this.savedServers.filter((x) => connectionID(x) !== connectionID(profile));
    Preferences.savedServers = this.savedServers;
    CredentialVault.save(undefined, connectionID(profile));
    CredentialVault.save(credential);
    this.server = profile;
    Preferences.server = profile;
    this.serverStatus = status;
    this.showReauth = false;
    if (!same) {
      this.hostWelcome = profile.endpoint.kind === "relay" ? relayHub.tunnel(profile.endpoint.relay).welcome : undefined;
      this.chats = [];
      this.sessions.clear();
      this.profiles.clear();
      this.selectedProfile = undefined;
      Preferences.selectedProfile = undefined;
      Preferences.lastSessionID = undefined;
      this.activeChat = this.makeChat(undefined);
    }
    this.assets.use(this.workspaceScope);
    this.route = "main";
    this.start(profile.endpoint, credential);
    this.changed();
  }

  signOut(): void {
    if (this.server?.endpoint.kind === "relay") relayHub.forget(this.server.endpoint.relay);
    // 还有别的连接：直接切过去
    const next = this.savedServers[0];
    if (next) {
      this.server = undefined;
      this.switchConnection(next);
      return;
    }
    this.stopClient();
    CredentialVault.save(undefined);
    this.server = undefined;
    Preferences.server = undefined;
    this.chats = [];
    this.sessions.clear();
    this.profiles.clear();
    this.connection = { kind: "idle" };
    Preferences.lastSessionID = undefined;
    this.activeChat = this.makeChat(this.selectedProfile);
    this.route = "onboarding";
    this.changed();
  }

  // MARK: 事件路由

  private handleEvent(event: GatewayEvent): void {
    const p = obj(event.payload);
    switch (event.type) {
      case EventType.gatewayReady:
        return;
      case EventType.sessionsChanged:
        this.sessions.scheduleRefresh(this.api, this.selectedProfile);
        return;
      case EventType.cronChanged:
        this.cronRevision += 1;
        this.changed();
        return;
      case "display.install.log": {
        const line = str(p?.line);
        if (line) { this.displayInstallLog = [...this.displayInstallLog, line].slice(-30); this.changed(); }
        return;
      }
      case "display.install.done": case "display.lease":
        this.displayRevision += 1;
        this.changed();
        return;
      case EventType.notificationShow: {
        const text = str(p?.text);
        if (text) this.showToast(text);
        return;
      }
      case EventType.sessionTitle: {
        // 帧上的 session_id 是实时 id，payload 里的 session_id 才是存储 id
        const stored = str(p?.session_id);
        const title = str(p?.title);
        if (stored && title) this.sessions.rename(stored, title);
        break;
      }
    }
    const chat = event.sessionID ? this.chats.find((c) => c.liveSessionID === event.sessionID) : undefined;
    if (!chat) return;
    const change = chat.handle(event);
    switch (change.type) {
      case "turnCompleted":
        this.turnCompleted(chat, change.text, change.status);
        break;
      case "sideTaskCompleted":
        if (!this.isVisible(chat)) this.notify(change.kind === "btw" ? "旁支问题已回答" : "后台任务完成", change.text, chat);
        break;
      case "toast":
        if (chat === this.activeChat) this.showToast(change.text);
        break;
    }
  }

  private turnCompleted(chat: ChatStore, text: string, status: string): void {
    this.sessions.scheduleRefresh(this.api, this.selectedProfile);
    if (!this.isVisible(chat) && Preferences.notifyOnCompletion && status !== "interrupted") {
      const bot = this.profiles.title(chat.profile);
      this.notify(`${bot} · ${chat.displayTitle}`, status === "error" ? `执行出错：${text}` : text, chat);
    }
  }

  private handleRequest(request: ServerRequest): void {
    if (request.method === "display.install.sudo") {
      this.displaySudoRequest = request;
      this.changed();
      return;
    }
    const sid = requestSessionID(request);
    const chat = sid ? this.chats.find((c) => c.liveSessionID === sid) : undefined;
    if (!chat) {
      // session.resume 的结果里带的 open_requests 会先于 resume 返回到达，此时对话还不知道自己的实时 id。
      // 先暂存，对话接管会话时认领；10 秒没人认领再拒绝，避免 agent 一直等待
      this.unclaimedRequests.push(request);
      setTimeout(() => {
        const i = this.unclaimedRequests.indexOf(request);
        if (i < 0) return;
        this.unclaimedRequests.splice(i, 1);
        this.client?.respond(request, methodNotFound);
      }, 10000);
      return;
    }
    this.deliver(request, chat);
  }

  /** 对话拿到实时会话 id 后调用，认领之前暂存的服务端请求 */
  claimRequests(chat: ChatStore): void {
    const sid = chat.liveSessionID;
    if (!sid) return;
    const mine = this.unclaimedRequests.filter((r) => requestSessionID(r) === sid);
    this.unclaimedRequests = this.unclaimedRequests.filter((r) => requestSessionID(r) !== sid);
    for (const r of mine) this.deliver(r, chat);
  }

  private deliver(request: ServerRequest, chat: ChatStore): void {
    chat.receive(request);
    if (this.isVisible(chat)) return;
    const params = obj(request.params);
    const body = request.method === "approval" ? `有一条命令等待你批准：${str(params?.command) ?? ""}`
      : request.method === "clarify" ? (str(params?.question) ?? "有问题需要你回答") : "需要你的输入";
    this.notify(`${this.profiles.title(chat.profile)} 需要你`, body, chat);
  }

  // MARK: 浏览器通知（页面在后台时）

  notificationsSupported(): boolean {
    return typeof Notification !== "undefined";
  }

  async requestNotificationPermission(): Promise<NotificationPermission | "unsupported"> {
    if (!this.notificationsSupported()) return "unsupported";
    if (Notification.permission !== "default") return Notification.permission;
    return Notification.requestPermission();
  }

  private notify(title: string, body: string, chat: ChatStore): void {
    if (!this.notificationsSupported() || Notification.permission !== "granted") return;
    const text = body.length > 160 ? body.slice(0, 160) + "…" : body;
    try {
      const n = new Notification(title, { body: text, tag: chat.storedSessionID ?? chat.id, icon: "/icons/icon-192.png" });
      n.onclick = () => {
        window.focus();
        if (chat.storedSessionID) this.openStored(chat.storedSessionID, chat.profile);
        n.close();
      };
    } catch {
      // 安卓 Chrome 不允许页面直接 new Notification，要经 Service Worker
      void navigator.serviceWorker?.ready.then((reg) => reg.showNotification(title, { body: text, tag: chat.storedSessionID ?? chat.id, icon: "/icons/icon-192.png" }));
    }
  }

  // MARK: 对话管理

  private makeChat(profile: string | undefined, storedID?: string, title?: string): ChatStore {
    const chat = new ChatStore(this, profile, storedID, title);
    this.chats = [...this.chats, chat];
    this.pruneChats();
    return chat;
  }

  newChat(): void {
    this.isSidebarOpen = false;
    this.selectedTab = "chat";
    const active = this.activeChat;
    if (active.isEmpty && active.profile === this.selectedProfile) { this.changed(); return; }
    this.activeChat = this.makeChat(this.selectedProfile);
    Preferences.lastSessionID = undefined;
    this.changed();
  }

  /** 以某个角色开始一段新对话，并切到“对话”页 */
  startRole(name: string, prompt: string): void {
    this.isSidebarOpen = false;
    this.activeChat = this.makeChat(this.selectedProfile);
    Preferences.lastSessionID = undefined;
    this.selectedTab = "chat";
    this.changed();
    void this.activeChat.startRole(name, prompt);
  }

  open(summary: SessionSummary): void {
    this.openStored(summary.id, this.selectedProfile, summary.title);
  }

  openStored(id: string, profile: string | undefined, title?: string): void {
    this.isSidebarOpen = false;
    this.presentedFeature = undefined;
    this.selectedTab = "chat";
    if (profile !== this.selectedProfile) this.setSelectedProfile(profile);
    const existing = this.chats.find((c) => c.storedSessionID === id);
    this.activeChat = existing ?? this.makeChat(profile, id, title);
    Preferences.lastSessionID = id;
    this.changed();
    void this.activeChat.loadIfNeeded();
  }

  /** 打开某个定时任务最近一次运行的结果（每次运行本身就是一个会话） */
  async openCronJob(jobID: string, profile: string | undefined): Promise<void> {
    try {
      const runs = await this.api?.cronRuns(jobID, profile, 1);
      const run = runs?.[0];
      if (run) this.openStored(run.id, profile, run.title);
      else this.showToast("这个定时任务还没有运行结果");
    } catch (e) {
      this.showToast(`打不开定时任务结果：${e instanceof Error ? e.message : String(e)}`);
    }
  }

  chatDidCreateSession(chat: ChatStore): void {
    if (chat === this.activeChat) Preferences.lastSessionID = chat.storedSessionID;
    this.sessions.scheduleRefresh(this.api, this.selectedProfile);
  }

  /** 主人删除一个 Bot。正在用它就先切回默认 Bot；connector 会顺带清掉它的邀请和访客 */
  async deleteBot(profile: ProfileSummary): Promise<void> {
    if (!this.api || profile.is_default) return;
    await this.api.deleteProfile(profile.name);
    if (this.selectedProfile === profile.name) this.selectProfile(undefined);
    await this.profiles.refresh(this.api);
    this.showToast(`已删除「${this.profiles.title(profile.name) || profile.name}」`);
  }

  selectProfile(name: string | undefined): void {
    if (name === this.selectedProfile) return;
    this.setSelectedProfile(name);
    this.newChat();
    void this.sessions.refresh(this.api, name);
    this.api?.commandsCatalog(undefined, name).then((c) => { this.slashCommands = c; this.changed(); }, () => {});
  }

  private setSelectedProfile(name: string | undefined): void {
    this.selectedProfile = name;
    Preferences.selectedProfile = name;
    this.assets.use(this.workspaceScope);
    this.changed();
  }

  async refreshAssets(): Promise<void> {
    this.assets.use(this.workspaceScope);
    await this.assets.refresh(this.api, this.selectedProfile);
  }

  /** 工作台内容属于哪台主机（哪个身份）的哪个 Bot */
  get workspaceScope(): string {
    return `${this.server ? connectionID(this.server) : ""}|${this.selectedProfile ?? "default"}`;
  }

  /** 删除对话：先从列表里移走，再请求服务器；失败了放回原处并提示 */
  async deleteSession(summary: SessionSummary): Promise<void> {
    const api = this.api;
    if (!api) return;
    const index = this.sessions.sessions.findIndex((x) => x.id === summary.id);
    this.sessions.remove(summary.id);
    const chat = this.chats.find((c) => c.storedSessionID === summary.id);
    if (chat) {
      this.chats = this.chats.filter((c) => c !== chat);
      if (this.activeChat === chat) this.activeChat = this.makeChat(this.selectedProfile);
      const live = chat.liveSessionID;
      if (live) { api.closeSession(live).catch(() => {}); this.client?.untrack(live); }
      this.changed();
    }
    try {
      await api.deleteSession(summary.id, this.selectedProfile);
      TranscriptCache.remove(summary.id);
    } catch (e) {
      this.sessions.restore(summary, index);
      this.showToast(`删除失败：${e instanceof Error ? e.message : String(e)}`);
    }
  }

  async renameActive(title: string): Promise<void> {
    const chat = this.activeChat;
    const sid = chat.liveSessionID;
    if (!sid || !title.trim()) return;
    try {
      await this.api?.renameSession(sid, title.trim());
      chat.title = title.trim();
      if (chat.storedSessionID) this.sessions.rename(chat.storedSessionID, chat.title);
      this.changed();
    } catch (e) {
      this.showToast(e instanceof Error ? e.message : String(e));
    }
  }

  /** 最多保留 12 个打开的对话，关闭最久没动静且空闲的 */
  private pruneChats(): void {
    const limit = 12;
    if (this.chats.length <= limit) return;
    const removable = this.chats
      .filter((c) => c !== this.activeChat && !c.isRunning && !c.pendingRequests.length)
      .sort((a, b) => a.lastActivity - b.lastActivity)
      .slice(0, this.chats.length - limit);
    for (const chat of removable) {
      this.chats = this.chats.filter((c) => c !== chat);
      const live = chat.liveSessionID;
      if (live) { this.client?.untrack(live); this.api?.closeSession(live).catch(() => {}); }
    }
  }

  private isVisible(chat: ChatStore): boolean {
    return this.isInForeground && chat === this.activeChat && this.selectedTab === "chat" && this.presentedFeature === undefined;
  }

  // MARK: 前后台

  private watchVisibility(): void {
    if (typeof document === "undefined") return;
    document.addEventListener("visibilitychange", () => {
      const visible = document.visibilityState === "visible";
      this.isInForeground = visible;
      if (visible && this.route === "main" && this.client) {
        // 手机上的网页在后台会被系统挂起，回来时连接可能已经悄悄断了：马上检查，断了就立刻重连
        relayHub.checkAll();
        this.client.probe();
        this.client.connect();
      }
      this.changed();
    });
    window.addEventListener("online", () => { if (this.route === "main") this.client?.reconnectNow(); });
  }

  // MARK: 界面状态

  setSidebarOpen(open: boolean): void { this.isSidebarOpen = open; this.changed(); }
  setTab(tab: MainTab): void { this.selectedTab = tab; this.isSidebarOpen = false; this.changed(); }
  present(feature: FeatureID | undefined): void { this.presentedFeature = feature; this.isSidebarOpen = false; this.changed(); }

  showToast(text: string): void {
    this.toast = { id: ++toastCounter, text };
    clearTimeout(this.toastTimer);
    this.toastTimer = setTimeout(() => { this.toast = undefined; this.changed(); }, 3000);
    this.changed();
  }

  dismissToast(): void { this.toast = undefined; this.changed(); }

  /** 有新版本可用（Service Worker 下载好了）：界面上显示“有新版本，点此刷新” */
  updateAvailable?: () => void;

  offerUpdate(apply: () => void): void {
    this.updateAvailable = apply;
    this.changed();
  }

  bumpCronRevision(): void { this.cronRevision += 1; this.changed(); }

  resetDisplayInstallLog(): void { this.displayInstallLog = []; this.changed(); }

  /** 回答（或拒绝）安装桌面组件要的 sudo 密码：只用这一次，不保存 */
  answerDisplaySudo(password: string | undefined): void {
    const request = this.displaySudoRequest;
    if (!request) return;
    this.displaySudoRequest = undefined;
    this.client?.respond(request, password === undefined ? { error: { code: -32000, message: "declined by user" } } : { result: { value: password } });
    this.changed();
  }

  /** 让 React 重新渲染（例如外部改了 server 相关的偏好） */
  touch(): void { this.changed(); }

  /** 当前连接的标识（作品缓存、列表刷新按它分开） */
  get connectionKey(): string { return this.server ? endpointKey(this.server.endpoint) : ""; }
}

function deviceName(): string {
  const ua = navigator.userAgent;
  const browser = /Edg\//.test(ua) ? "Edge" : /Firefox\//.test(ua) ? "Firefox" : /Chrome\//.test(ua) ? "Chrome" : /Safari\//.test(ua) ? "Safari" : "浏览器";
  const device = /iPhone/.test(ua) ? "iPhone" : /iPad/.test(ua) ? "iPad" : /Android/.test(ua) ? "安卓" : /Mac OS X/.test(ua) ? "Mac" : /Windows/.test(ua) ? "Windows" : "";
  const standalone = window.matchMedia?.("(display-mode: standalone)").matches;
  return `${device ? device + " " : ""}${standalone ? "网页 App" : browser}`.trim();
}
